import { CancelToken, isCancel } from 'axios'

import { getAppConfig } from 'deploy-env/app-config'
import RequestWrapper from 'e-submission/domain/data/request-wrapper'

const pending = {}

/**
 * handleRequestCancel
 * @param user
 * @param config
 * @param defaultValue
 */
async function handleRequestCancel(user, config, defaultValue = undefined) {
  const requestIdentifier = config.requestIdentifier

  if (requestIdentifier) {
    if (requestIdentifier in pending) {
      const canceler = pending[requestIdentifier]
      canceler.cancel('cancel previous request')
    }

    const newCancelToken = CancelToken.source()
    config.cancelToken = newCancelToken.token
    pending[requestIdentifier] = newCancelToken
  }

  try {
    config.url = new URL(config.url, getAppConfig().ADVISORZONE_MIDDLEWARE).toString()
    let result = await RequestWrapper.request(user, config)
    if (requestIdentifier) {
      delete pending[requestIdentifier]
    }

    return result.data
  } catch (e) {
    if (isCancel(e) || e.message === "Cannot read properties of undefined (reading 'url')") {
      return defaultValue
    }
    throw e
  }
}

async function checkAvailable() {
  try {
    const response = await RequestWrapper.getRequest('partner-api/status', {}, {}, { timeout: 5000 })
    return response.data.enable
  } catch (e) {
    console.error(e)
    return false
  }
}

// /////////////////////////////////////////////////////////////////////////////
// Product Api
// /////////////////////////////////////////////////////////////////////////////

async function getProductCategories(user, agentType, agentCode) {
  return await handleRequestCancel(user, {
    method: 'get',
    url: 'partner-api/product/categories',
    params: {
      agentCode: agentCode || '',
      agentType: agentType,
    },
    requestIdentifier: 'get_product_categories',
  })
}

async function getProductPackage(user, payload) {
  return await handleRequestCancel(user, {
    method: 'post',
    url: 'partner-api/product/package',
    data: payload,
    requestIdentifier: 'get_product_package',
  })
}

async function getProductFund(user, params) {
  return await handleRequestCancel(user, {
    method: 'get',
    url: 'partner-api/product/funds',
    params: params,
    requestIdentifier: 'get_product_funds',
  })
}

// /////////////////////////////////////////////////////////////////////////////
// Occupation Api
// /////////////////////////////////////////////////////////////////////////////

async function getOccupations(birthdate, user, requestIdentifier) {
  return handleRequestCancel(
    user,
    {
      method: 'GET',
      url: 'partner-api/occupations',
      requestIdentifier,
    },
    []
  )
}

// /////////////////////////////////////////////////////////////////////////////
// Address Api
// /////////////////////////////////////////////////////////////////////////////

async function getNationality() {
  return handleRequestCancel(
    undefined,
    {
      method: 'get',
      url: 'partner-api/nationality',
    },
    []
  )
}

// /////////////////////////////////////////////////////////////////////////////
// RPQ Api
// /////////////////////////////////////////////////////////////////////////////

async function getFnaConfiguration(user) {
  return await handleRequestCancel(user, {
    method: 'GET',
    url: 'partner-api/fna-configuration',
  })
}

async function getRPQQuestionnaire(user) {
  return await handleRequestCancel(user, {
    method: 'GET',
    url: 'partner-api/question/rpq',
  })
}

async function generateRPQDocument(user, payload) {
  return await handleRequestCancel(user, {
    method: 'POST',
    url: 'partner-api/generate/rpq',
    data: payload,
    requestIdentifier: 'generate_rpq_document',
  })
}

async function generateRADocument(user, payload) {
  return await handleRequestCancel(user, {
    method: 'POST',
    url: 'partner-api/generate/ra',
    data: payload,
    requestIdentifier: 'generate_ra_document',
  })
}

async function getKYCQuestionnaire(user) {
  return await handleRequestCancel(user, {
    method: 'GET',
    url: 'partner-api/question/kyc',
  })
}

// /////////////////////////////////////////////////////////////////////////////

async function calculateWithValidation(user, payload) {
  return await handleRequestCancel(user, {
    method: 'POST',
    url: 'partner-api/validation',
    data: payload,
  })
}

async function calculateWithPolicyValue(user, payload) {
  return await handleRequestCancel(user, {
    method: 'POST',
    url: 'partner-api/generate/policy',
    data: payload,
    requestIdentifier: 'generate_policy',
  })
}

async function generateSaleProposal(user, payload) {
  return await handleRequestCancel(user, {
    method: 'POST',
    url: 'partner-api/proposal/generate',
    data: payload,
    requestIdentifier: 'generate_sale_proposal',
  })
}

// process-application /////////////////////////////////////////////////////////////////////////////////////////////////

async function createPolicy(user, payload) {
  return await handleRequestCancel(user, {
    method: 'POST',
    url: 'partner-api/application/policy/create',
    data: payload,
    requestIdentifier: 'create_app_policy',
  })
}

async function updatePolicy(user, policyId, payload) {
  return await handleRequestCancel(user, {
    method: 'POST',
    url: `partner-api/application/policy/${policyId}/update`,
    data: payload,
    requestIdentifier: 'update_app_policy',
  })
}

async function listDocument(user, policyNumber) {
  return await handleRequestCancel(user, {
    method: 'GET',
    url: `partner-api/application/policy/${policyNumber}/documents`,
    requestIdentifier: 'list_policy_documents',
  })
}

async function downloadPolicyDocument(user, payload) {
  return await handleRequestCancel(user, {
    method: 'POST',
    url: `partner-api/application/policy/${payload.data.policyNumber}/download`,
    data: payload,
    requestIdentifier: `download_policy_${payload.data.policyNumber}_${payload.data.fileType}`,
  })
}

export const partnerApiRequest = {
  checkAvailable,
  getProductCategories,
  getProductPackage,
  getProductFund,
  getOccupations,
  getNationality,
  getFnaConfiguration,
  getRPQQuestionnaire,
  generateRPQDocument,
  generateRADocument,
  getKYCQuestionnaire,
  calculateWithValidation,
  calculateWithPolicyValue,
  generateSaleProposal,
  createPolicy,
  updatePolicy,
  listDocument,
  downloadPolicyDocument,
}
